/// <amd-module name="TFS/DRITools/Components/Callgraph/CallgraphView" />
import * as React from "react";

// Office Fabric
import { Label } from "OfficeFabric/Label";
import { Button } from "VSSUI/Button";
import { TextField } from "VSSUI/TextField";

// VSS
import { ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";

// Resources
import * as Resources from "TFS/DRITools/Resources";

// DRITools
import ParserTab from "TFS/DRITools/Components/Callgraph/ParserTab";
import WaterfallTable from "TFS/DRITools/Components/Callgraph/WaterfallTable";
import { ErrorMessage } from "TFS/DRITools/Components/Common/Error";
import { Loading } from "TFS/DRITools/Components/Common/Loading";
import { CallgraphResults } from "TFS/DRITools/Contracts/DRITools";
import { IDRIToolsRestClient } from "TFS/DRITools/RestClient/DRITools";
import { RequestNode } from "TFS/DRITools/util/RequestNode";
import { parseTrace } from "TFS/DRITools/util/TraceParser";

export interface ICallgraphViewProps extends IVssComponentProperties {
    selectedColumns: string[];
}

export interface ICallgraphViewState extends IVssComponentProperties {
    currentContext?: RequestNode[] | undefined;
    disabled: boolean;
    selectedTable: string;
    activityId: string;
    data?: RequestNode[];
    depth?: number;
    startTime?: number;
    isCalloutVisible: boolean;
    waterfallLength: number;
    loading: boolean;
    error: string;
}

class CallgraphView extends VssComponent<ICallgraphViewProps, ICallgraphViewState> {
    private _driToolClient: IDRIToolsRestClient;
    private _requestId = new ObservableValue<string>("");
    private _inputChange = {
        id: (event: React.ChangeEvent<HTMLInputElement>, value: string): void => {
            this._requestId.value = value;
        }
    };

    constructor(props: ICallgraphViewProps, context: IVssComponentContext) {
        super(props, context);

        this._driToolClient = this.context.pageContext.getRestClient<IDRIToolsRestClient>("IDRIToolsRestClient");
        this.state = {
            disabled: true,
            activityId: "",
            selectedTable: "",
            isCalloutVisible: false,
            loading: false,
            waterfallLength: 0,
            error: "",
            currentContext: undefined
        };
    }

    public render(): JSX.Element {
        const { loading, error } = this.state;
        const visualization: JSX.Element = this._renderVisualization();
        return (
            <div className="callgraph-hub">
                <form className="id-form" onSubmit={this._loadCallgraph}>
                    <Label>{Resources.Callgraph_Label_TextInput}</Label>
                    <TextField
                        className="request-id-text-field"
                        value={this._requestId}
                        onChange={this._inputChange.id}
                        disabled={false}
                        required={true}
                        readOnly={false}
                    />
                    <br />
                    <div className="flex flex-grow">
                        <Button primary={true} type="submit" text={Resources.Callgraph_PrimaryButtonText} />
                    </div>
                </form>
                <br />
                <div className="flex flex-center">{loading ? <Loading /> : error !== "" ? <ErrorMessage error={error} /> : visualization}</div>
            </div>
        );
    }

    public componentWillUnmount(): void {
        window.addEventListener("resize", () => {});
    }

    public componentDidMount(): void {
        window.addEventListener("resize", () => {
            this.forceUpdate();
        });
    }

    private _loadCallgraph = async (event: React.MouseEvent<HTMLElement> | Event | React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        this.setState({
            isCalloutVisible: false,
            loading: true,
            waterfallLength: 0,
            error: "",
            currentContext: undefined
        });

        // Call the Callgraph API
        return this._driToolClient
            .getCallGraphData(this._requestId.value)
            .then(
                (results: CallgraphResults): void => {
                    if (!results || !results.activityLog || !results.iisLogs || !results.productTrace) {
                        throw new Error(Resources.Callgraph_Error_BrokenId);
                    }
                    if (results.activityLog.length === 0 && results.iisLogs.length === 0 && results.productTrace.length === 0) {
                        throw new Error(Resources.Callgraph_Error_EmptyRequest);
                    }
                    this.setState({ loading: false, error: "" });
                    this._transformData(results);
                }
            )
            .catch(
                (err): void => {
                    this.setState({ loading: false, error: err });
                }
            );
    };

    /**
     * Transforms the data from API to have parsable format
     * @param results raw data from callgraph api
     */
    private _transformData(results: CallgraphResults): void {
        // Dictionary of all requests and their properties
        const byID: { [key: string]: RequestNode } = {};

        // Save activityLog by ID
        for (const activityLog of results.activityLog) {
            byID[activityLog.ActivityId] = {
                ActivityLog: activityLog,
                IisLog: {},
                ProductTrace: [],
                children: [],
                parentId: "",
                depth: 1
            };
        }

        // Save IisLogs by ID
        for (const iisLog of results.iisLogs) {
            if (byID[iisLog.ActivityId]) {
                byID[iisLog.ActivityId].IisLog = iisLog;
            } else {
                byID[iisLog.ActivityId] = {
                    ActivityLog: {},
                    IisLog: iisLog,
                    ProductTrace: [],
                    children: [],
                    parentId: "",
                    depth: 1
                };
            }
        }

        // Save ProductTrace by ID
        for (const productTrace of results.productTrace) {
            if (byID[productTrace.ActivityId]) {
                byID[productTrace.ActivityId].ProductTrace.push(productTrace);
                // If there's a 36109 message, add a jsx element to this id (no need to parse every time you click)
                if (productTrace.Tracepoint == Resources.Callgraph_36109) {
                    const item = byID[productTrace.ActivityId];
                    if (!item.trace) {
                        item.trace = [];
                    }
                    try {
                        item.trace.push(<ParserTab data={parseTrace(productTrace.Message.toString())} />);
                        item.hasSlowCommandTrace = true;
                    } catch (e) {
                        item.hasSlowCommandTrace = false;
                    }
                }
            }
        }

        // Array to sort by time
        const data: RequestNode[] = Object.keys(byID).map(key => byID[key]);

        if (data.length === 0) {
            return;
        }

        // Sorts by activitylog startTime or iisLog precise time stamp
        data.sort(
            (a: RequestNode, b: RequestNode): number => {
                const dateA: Date = a.ActivityLog.StartTime ? a.ActivityLog.StartTime : a.IisLog.PreciseTimeStamp;
                const dateB: Date = b.ActivityLog.StartTime ? b.ActivityLog.StartTime : b.IisLog.PreciseTimeStamp;
                return dateA.getTime() - dateB.getTime();
            }
        );

        let maxDepth = 1;
        let maxLength = 0;
        const startTime: number = data[0].ActivityLog.StartTime
            ? Date.parse(data[0].ActivityLog.StartTime) + data[0].ActivityLog.StartTime.getMilliseconds()
            : Date.parse(data[0].IisLog.PreciseTimeStamp) + data[0].IisLog.PreciseTimeStamp.getMilliseconds();

        // Make a tree structure from the id's, determine depth of each element.
        for (let i = 0; i < data.length; i++) {
            const request: RequestNode = data[i];
            const endTime: number = request.ActivityLog.StartTime
                ? Date.parse(request.ActivityLog.StartTime) +
                  request.ActivityLog.StartTime.getMilliseconds() +
                  request.ActivityLog.ExecutionTime / 1000
                : Date.parse(request.IisLog.PreciseTimeStamp) + request.IisLog.PreciseTimeStamp.getMilliseconds();
            if (endTime > maxLength) {
                maxLength = endTime;
            }
            const parentId: string = request.ActivityLog.RelatedActivityId ? request.ActivityLog.RelatedActivityId : request.IisLog.X_TFS_Session;
            const depth: number =
                (parentId !== (request.ActivityLog.ActivityId ? request.ActivityLog.ActivityId : request.IisLog.ActivityId) && byID[parentId]
                    ? byID[parentId].depth
                    : 0) + 1;
            if (depth !== 1) {
                request.depth = depth;
                byID[parentId].children.push(request);
                data.splice(i, 1);
                i--;
            }
            if (depth > maxDepth) {
                maxDepth = depth;
            }
        }
        const output: RequestNode[] = [];
        while (data.length > 0) {
            this._getChildren(data[0], output);
            data.shift();
        }

        this.setState({ currentContext: output, depth: maxDepth, startTime: startTime, waterfallLength: maxLength - startTime });
    }

    /**
     * Creates a pre-traversal array from the tree
     * @param data parentNode
     * @param output the data in the list format
     */
    private _getChildren(data: RequestNode, output: RequestNode[]): void {
        if (data) {
            output.push(data);
            for (let i = 0; i < data.children.length; i++) {
                this._getChildren(data.children[i], output);
            }
        }
    }

    private _renderVisualization(): JSX.Element {
        const { currentContext, depth, startTime, waterfallLength } = this.state;
        const { selectedColumns } = this.props;
        return (
            <div>
                {currentContext && depth && startTime ? (
                    <WaterfallTable
                        barHeight={40}
                        currentMax={5000}
                        selectedColumns={selectedColumns}
                        parseTrace={true}
                        waterfallLength={waterfallLength}
                        data={currentContext}
                        depth={depth}
                        startTime={startTime}
                    />
                ) : null}
            </div>
        );
    }
}

export default CallgraphView;
